import { UploadSimple, XCircle } from "@phosphor-icons/react";
import { Progress, Text, Flex, Grid } from "@radix-ui/themes";
import cx from "classnames";
import { useCallback } from "react";

export interface UploadProgressData {
  totalParts: number;
  completedParts: number;
  totalBytes: number;
  uploadedBytes: number;
}

export interface UploadProgressProps {
  isUploading: boolean;
  progress?: UploadProgressData;
  fileName?: string;
  error?: string;
  isComplete?: boolean;
  onCancel?: () => void;
  variant?: "overlay" | "inline" | "minimal";
  className?: string;
  fixedWidth?: boolean;
}

/* eslint-disable react/prop-types */
export const UploadProgress: React.FC<UploadProgressProps> = ({
  isUploading,
  progress,
  fileName,
  error,
  isComplete,
  onCancel,
  variant = "overlay",
  className,
  fixedWidth,
}) => {
  // Calculate progress percentage
  const progressPercentage = progress
    ? Math.round((progress.uploadedBytes / progress.totalBytes) * 100)
    : 0;

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }, []);

  if (!isUploading && !error && !isComplete) {
    return null;
  }

  if (error) {
    return (
      <div
        className={cx(
          "border-red-200 bg-red-50 text-red-700 flex items-center gap-3 rounded-lg border p-3",
          className,
        )}
        role="alert"
        aria-live="assertive"
        data-testid="upload-progress-error"
      >
        <XCircle size={20} className="text-red-700 shrink-0" />
        <div className="text-red-700 flex min-w-0 flex-1 flex-col">
          <Text size="2" className="font-medium">
            Upload failed
          </Text>
          {fileName && (
            <Text
              size="1"
              className="truncate"
              data-testid="upload-progress-file-name"
            >
              {fileName}
            </Text>
          )}
          {error && (
            <Text
              size="1"
              className="mt-1"
              data-testid="upload-progress-error-message"
            >
              {error}
            </Text>
          )}
        </div>
      </div>
    );
  }

  if (variant === "minimal") {
    return (
      <div
        className={cx("flex w-full items-center justify-between", className)}
        data-testid="upload-progress-minimal"
      >
        <div style={{ width: 105 }}>
          <Progress
            size="1"
            value={progressPercentage}
            data-testid="upload-progress-bar"
          />
        </div>
        <Text
          size="1"
          className="text-gray-600 ml-2"
          style={{ minWidth: 28, textAlign: "right" }}
        >
          {progressPercentage}%
        </Text>
        <div
          className="sr-only"
          aria-live="polite"
          aria-label={`Upload progress: ${progressPercentage}%`}
        >
          {isComplete
            ? "Upload completed successfully"
            : `${progressPercentage}% complete`}
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={cx("flex w-full flex-col items-center space-y-2", className)}
        data-testid="upload-progress-inline"
      >
        {/* Progress bar with fixed width if requested */}
        <div style={{ width: fixedWidth ? 220 : "100%" }}>
          <Progress
            size="2"
            value={progressPercentage}
            data-testid="upload-progress-bar"
          />
          {/* Below bar: file size left, percent right */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <Text size="1" className="text-gray-600">
              {formatFileSize(progress?.uploadedBytes || 0)} /{" "}
              {formatFileSize(progress?.totalBytes || 0)}
            </Text>
            <Text
              size="1"
              className="text-gray-600"
              style={{ minWidth: 40, textAlign: "right" }}
            >
              {progressPercentage}%
            </Text>
          </div>
        </div>
        <div
          className="sr-only"
          aria-live="polite"
          aria-label={`Upload progress: ${progressPercentage}% complete`}
        >
          {isComplete
            ? `Upload completed successfully`
            : `${progressPercentage}% complete`}
        </div>
      </div>
    );
  }

  // Overlay variant (default) - matches the existing design system
  return (
    <div
      data-testid="upload-progress-overlay"
      className={cx(
        "fixed inset-x-0 bottom-0 z-50 flex h-1/3 flex-col justify-end bg-gradient-to-t from-violet-violet7 to-transparent text-puntt-accent-10 backdrop-blur-sm transition-all",
        className,
      )}
      style={{
        maskImage: "linear-gradient(to top, black 50%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to top, black 50%, transparent 100%)",
      }}
      role="dialog"
      aria-label="Upload progress"
      aria-live="polite"
    >
      <div className="m-6 flex flex-col items-center justify-end gap-6 rounded-xl border-2 border-dashed border-violet-violet8 p-6">
        <UploadSimple size={36} />

        <Grid gap="2" width="400px">
          <Text
            size="6"
            className="text-wrap text-center"
            style={{
              textShadow:
                "0 0 5px rgba(111, 111, 223, 0.5), 0 0 10px rgba(111, 111, 223, 0.4), 0 0 15px rgba(111, 111, 223, 0.3)",
            }}
            data-testid="upload-progress-file-name"
          >
            {fileName || "Uploading..."}
          </Text>

          <Progress
            size="3"
            value={progressPercentage}
            data-testid="upload-progress-bar"
          />

          <Flex align="center" justify="between" className="text-sm">
            <Text size="1" className="text-gray-600">
              {formatFileSize(progress?.uploadedBytes || 0)} /{" "}
              {formatFileSize(progress?.totalBytes || 0)}
            </Text>
            <Flex gap="3" align="center">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="text-gray-500 hover:text-gray-700 text-sm underline"
                  data-testid="upload-progress-cancel"
                >
                  Cancel
                </button>
              )}
            </Flex>
          </Flex>
        </Grid>

        {/* Screen reader announcements */}
        <div
          className="sr-only"
          aria-live="polite"
          aria-label={`Upload progress for ${fileName || "file"}: ${progressPercentage}% complete`}
        >
          {isComplete
            ? `${fileName || "File"} upload completed successfully`
            : `${fileName || "File"} upload: ${progressPercentage}% complete
               ${
                 progress &&
                 progress.totalParts > 1 &&
                 `, part ${progress.completedParts} of ${progress.totalParts}`
               }
               ${
                 progress &&
                 progress.uploadedBytes > 0 &&
                 `, ${formatFileSize(progress.uploadedBytes)} of ${formatFileSize(progress.totalBytes)} uploaded`
               }`}
        </div>
      </div>
    </div>
  );
};
